// Pure-function pricing helpers for the Quote builder (M2 Phase 5.4+).
//
// Pricing model — read this before touching the math:
//   - Each part on a quote has a frozen `pricePerTier[t]` stored in
//     quote.partQuotedSpecs[partId]. That snapshot is set when the part is
//     added to the quote and stays sticky from then on — re-pricing a part
//     against the latest part record is an explicit user action, not a
//     side effect of opening the quote.
//   - The quote total for tier `t` is the sum of those frozen prices across
//     all parts on the quote. Prices are already per-unit AT that tier
//     quantity, so we don't multiply by qty here.
//   - Material costs come from the *current* part record's materialCost —
//     they're "live-ish" so the margin warning surfaces drift between the
//     snapshotted price and today's cost basis. The gap between sticky
//     prices and live costs is intentional: if the mill quote you got
//     Tuesday is 12% higher than what you priced the quote off of last
//     month, the margin alert should fire.
//
// Costing-mode fallback (see normalizeMaterialCostPerPiece):
//   MaterialCost has FIVE costing shapes (perPiece, perBar, perSheet,
//   perPack+packSize, perPack alone) with no discriminator field. Old code
//   only read perPiece and silently reported $0 for per-bar/sheet/pack
//   parts → margins appeared 100%. We now fall back through the modes:
//   per_piece → per_bar → per_sheet → per_pack/packSize → per_pack raw.
//   For per_bar / per_sheet / lone per_pack we treat the bulk price AS the
//   per-piece cost (placeholder pending Part.yieldPerStock) — that's
//   conservative (over-counts cost, under-counts margin) but it's better
//   than the $0 silent-mask we had.
//
// "No data" vs "0% margin":
//   computeMarginsByTier returns `(number | null)[]`. `null` means we have
//   no data for that tier (no prices summed AND no costs summed). A real
//   0% margin (priced exactly at cost) returns 0. The UI should treat
//   null as "tier not priced yet" and skip the margin-warning check.
//
// Runtime cost / shop overhead estimation is deliberately not modeled here
// — that's a richer pricing pass after the data flow is right.
//
// All functions are pure and take their inputs explicitly so they're easy
// to unit-test and to call from both the builder (live re-render) and the
// PDF/email exporters (frozen-at-render time).

import type { Machine, MaterialCost, Part, Quote } from './crmTypes';

/**
 * Margin below this triggers `marginWarning` on a quote. Surfaced as a
 * constant so the builder UI, the warning pill, and a future state-driven
 * threshold all read from the same place.
 *
 * Per spec/14 this is a per-org setting (state.ui.minMarginPct) — we hard-
 * code 25% here until that setting is wired through.
 */
export const MARGIN_WARNING_THRESHOLD = 25;

/**
 * Sum the frozen per-tier price across every part on the quote. Returns an
 * array the same length as quote.qtyTiers so callers can zip it with the
 * tier-quantity labels without length juggling.
 *
 * Missing snapshots (a partId without a partQuotedSpecs entry, or a tier
 * index past pricePerTier.length) contribute 0 — keeps the math forgiving
 * for half-built quotes.
 */
export function computeTotalsByTier(quote: Quote): number[] {
  const tierCount = quote.qtyTiers?.length ?? 0;
  const totals = new Array<number>(tierCount).fill(0);
  if (tierCount === 0) return totals;

  for (const partId of quote.partIds ?? []) {
    const spec = quote.partQuotedSpecs?.[partId];
    const prices = spec?.pricePerTier ?? [];
    for (let t = 0; t < tierCount; t++) {
      totals[t] += prices[t] ?? 0;
    }
  }
  return totals;
}

/**
 * Normalize a MaterialCost record down to a single per-piece dollar amount
 * for the given tier. Reads through the five costing modes in priority
 * order: per_piece (tier-aware) → per_bar → per_sheet → per_pack/packSize →
 * per_pack raw. Returns 0 only when none of the modes are set.
 *
 * `qtyAtTier` is accepted but unused today — it's here so the per_bar /
 * per_sheet / per_pack branches can divide by a future Part.yieldPerStock
 * (pieces-per-bar etc.) without churning the signature again.
 */
function normalizeMaterialCostPerPiece(
  mc: MaterialCost,
  tierIndex: number,
  _qtyAtTier: number,
): number {
  // per_piece is the only mode that's already per-tier — prefer it.
  const perPieceAtTier = mc.perPiece?.[tierIndex];
  if (perPieceAtTier != null && perPieceAtTier > 0) return perPieceAtTier;

  // per_bar / per_sheet — TODO yield calc when Part.yieldPerStock lands.
  // Until then we treat the bulk price as the per-piece cost (over-counts
  // cost → under-counts margin, conservative direction for warnings).
  if (mc.perBar != null && mc.perBar > 0) return mc.perBar;
  if (mc.perSheet != null && mc.perSheet > 0) return mc.perSheet;

  // per_pack — if packSize is set we can actually divide cleanly; otherwise
  // fall through to raw perPack as another placeholder (same caveat as bar).
  if (mc.perPack != null && mc.perPack > 0) {
    if (mc.packSize != null && mc.packSize > 0) {
      return mc.perPack / mc.packSize;
    }
    return mc.perPack;
  }

  return 0;
}

/**
 * Sum the *current* per-tier material cost across every part on the quote.
 * Used as the cost basis for live margin warnings — see file header.
 *
 * `partsById` is what's loaded in the builder's parts map. A missing
 * partId / missing materialCost contributes 0 for the affected tier; for
 * parts with materialCost we route through normalizeMaterialCostPerPiece
 * so per-bar/per-sheet/per-pack parts no longer silently report $0.
 *
 * CSM (customer-supplied material) — when `part.customerSuppliedMaterial`
 * is true, the customer brought the stock and KMF spent $0 on material.
 * For COST BASIS (margin calc) we contribute 0, regardless of what's in
 * materialCost. The handling fee charged for CSM is revenue — surfaced
 * via the part's `csmHandlingFeePerPiece` and folded into the price snap-
 * shot at quote time — not a cost. Mirrors v1's `materialOutOfPocketAt`
 * (index.html ~5558-5560) which also short-circuits CSM to $0.
 */
export function computeMaterialCostByTier(
  quote: Quote,
  partsById: Record<string, Part>,
): number[] {
  const tierCount = quote.qtyTiers?.length ?? 0;
  const costs = new Array<number>(tierCount).fill(0);
  if (tierCount === 0) return costs;

  const qtyTiers = quote.qtyTiers ?? [];
  for (const partId of quote.partIds ?? []) {
    const part = partsById[partId];
    if (!part) continue;
    // CSM short-circuit: customer brought the material → $0 cost to KMF
    // for every tier on this part. Skip the materialCost lookup entirely.
    if (part.customerSuppliedMaterial) continue;
    const mc = part.materialCost;
    if (!mc) continue;
    for (let t = 0; t < tierCount; t++) {
      costs[t] += normalizeMaterialCostPerPiece(mc, t, qtyTiers[t] ?? 0);
    }
  }
  return costs;
}

/**
 * Compute per-tier margin % from totals + costs. Rounded to one decimal.
 *
 * Returns `null` for a tier when we have NO DATA — neither a priced total
 * nor a cost basis. That lets the UI distinguish "no parts priced yet"
 * (don't warn, badge as empty) from "priced at exactly cost" (real 0%,
 * warn aggressively). Callers should null-check before comparing against
 * MARGIN_WARNING_THRESHOLD.
 *
 * Edge case: total = 0 but cost > 0 returns a real negative margin
 * (technically -Infinity, clamped to -100 here so the UI can render it).
 * That's "you gave it away with material cost on the books" — worth
 * surfacing, not hiding.
 */
export function computeMarginsByTier(
  totals: number[],
  costs: number[],
): (number | null)[] {
  const len = Math.max(totals.length, costs.length);
  const out = new Array<number | null>(len).fill(null);
  for (let t = 0; t < len; t++) {
    const total = totals[t] ?? 0;
    const cost = costs[t] ?? 0;

    // No data on either side → null. UI uses this to skip the warning.
    if (total === 0 && cost === 0) {
      out[t] = null;
      continue;
    }

    // Cost on the books but no price → clamp to -100% rather than -Infinity
    // so the UI can render a number. Still loud enough to surface.
    if (total === 0) {
      out[t] = -100;
      continue;
    }

    const margin = ((total - cost) / total) * 100;
    out[t] = Math.round(margin * 10) / 10;
  }
  return out;
}

/**
 * Inspect a quote + its parts map and report two completeness facts the UI
 * needs to badge things cleanly without re-implementing the cost/margin
 * logic:
 *
 *   - `tiersWithoutData`: tier indices where NO part contributes a price
 *     AND no part contributes a cost. Same condition that produces a null
 *     in computeMarginsByTier — exposed here so a "Tier 2: no data" badge
 *     can sit next to the tier column without round-tripping through the
 *     margin array.
 *   - `partsWithoutCost`: partIds where the current part record has no
 *     materialCost at all, OR has a materialCost shape that normalizes to
 *     0 across all tiers (every mode empty / zero). These are the parts
 *     that drag the cost basis to $0 and inflate margin.
 *
 * Pure read — does not mutate inputs.
 */
export function analyzePricingCompleteness(
  quote: Quote,
  partsById: Record<string, Part>,
): { tiersWithoutData: number[]; partsWithoutCost: string[] } {
  const tierCount = quote.qtyTiers?.length ?? 0;
  const qtyTiers = quote.qtyTiers ?? [];
  const partIds = quote.partIds ?? [];

  const totals = computeTotalsByTier(quote);
  const costs = computeMaterialCostByTier(quote, partsById);

  const tiersWithoutData: number[] = [];
  for (let t = 0; t < tierCount; t++) {
    if ((totals[t] ?? 0) === 0 && (costs[t] ?? 0) === 0) {
      tiersWithoutData.push(t);
    }
  }

  const partsWithoutCost: string[] = [];
  for (const partId of partIds) {
    const part = partsById[partId];
    const mc = part?.materialCost;
    if (!mc) {
      partsWithoutCost.push(partId);
      continue;
    }
    // Cost the part across every tier; if every tier normalizes to 0 it's
    // effectively uncosted regardless of which optional fields are set.
    let anyPositive = false;
    for (let t = 0; t < tierCount; t++) {
      if (normalizeMaterialCostPerPiece(mc, t, qtyTiers[t] ?? 0) > 0) {
        anyPositive = true;
        break;
      }
    }
    if (!anyPositive) partsWithoutCost.push(partId);
  }

  return { tiersWithoutData, partsWithoutCost };
}

// =====================================================
// Live cost engine (v1) — used by the seed/reprice path
// =====================================================
//
// Everything above this line is the SNAPSHOT-BASED view: it reads frozen
// pricePerTier off quote.partQuotedSpecs and materialCost off the Part
// record. Good for the "what did we promise" math. Bad for "what does this
// part actually cost us today" math — there's no setup amortization, no
// machine runtime, no outside-service folding.
//
// The functions below ARE the live cost engine. They walk the Part record,
// pull machine rates from a passed-in Machine registry, and produce a
// per-piece cost breakdown that the snapshot can be seeded from. The seed
// flow (QuoteDetailPage.seedSnapshotFromPart) calls these once per tier
// and freezes the result into partQuotedSpecs.pricePerTier.
//
// Why machines are passed in by NAME, not id:
//   Part.machines: MachineSetup[] carries `machine: string` (the machine's
//   display name, v1 freeform). The typed Machine entity has its own id,
//   but Part still references it by name. Until the wizard wires a typed
//   ref onto MachineSetup, we look the Machine up in the registry by name.
//   Caller is expected to build the map as: machines.reduce((m, x) => (m[x.name] = x, m), {})
//
// Missing machines (a MachineSetup.machine that isn't in machinesByName)
// contribute $0 for that machine line — we don't throw. That's the same
// forgiveness pattern the snapshot helpers use for missing parts. The
// `analyzePricingCompleteness` analog for the cost engine isn't built yet;
// if you need "which parts have machines without rates", surface it from
// the breakdown.

/**
 * Default markup multiplier. 1.65 ≈ 38% margin. Mirrors the per-org default
 * Justin wants until a state.ui.defaultMarkupPct setting lands (same pattern
 * as MARGIN_WARNING_THRESHOLD above).
 */
export const DEFAULT_MARKUP_PCT = 1.65;

/**
 * Per-piece cost breakdown. Each line is dollars/piece at the asked-for
 * tier. Sum is `totalCostPerPiece`. Keep all five lines on the result even
 * when zero so the UI can render line-items consistently.
 */
export interface PartCostBreakdown {
  materialPerPiece: number;
  setupAmortPerPiece: number;
  runPerPiece: number;
  outsideSetupPerPiece: number;
  outsidePerPiece: number;
  totalCostPerPiece: number;
}

/**
 * Compute the live cost-per-piece for a part at a given tier qty.
 *
 * Cost model (see spec on the PR / project notes — same shape as v1's
 * pricing pass, just typed):
 *
 *   materialPerPiece = CSM ? csmHandlingFeePerPiece
 *                          : materialCost.perPiece[tier]   (normalized)
 *   setupAmortPerPc  = sum(machines[].setupHrs * machine.setupRate) / qty
 *                      + (part.setupHrs (legacy) * defaultSetupRate) / qty
 *   runPerPc         = sum(machines[].hrsPerTier[tier] * machine.runRate)
 *   outsideSetupPerPc = sum(outsideServices[].setupCost) / qty
 *   outsidePerPc     = sum(outsideServices[].perPieceCost)
 *
 * Notes:
 *   - `qtyAtTier <= 0` (defensive — UI shouldn't allow it but a half-typed
 *     tier field could) treats setup as $0 to avoid Infinity. Run cost
 *     still uses the per-piece value from the part.
 *   - CSM short-circuits material to the handling fee — same convention
 *     as computeMaterialCostByTier (but here the FEE counts toward cost
 *     so the markup math stays correct; the snapshot consumer can decide
 *     whether to count it as revenue separately).
 *   - Missing machine in `machinesByName` → that machine's setup AND run
 *     lines contribute $0 (we can't compute without a rate). Loud-fail
 *     would block the seed call; we prefer silent-skip + a future "parts
 *     with unmapped machines" inspector.
 *   - `tier` is an index into machine.hrsPerTier / materialCost.perPiece.
 *     Out-of-range → 0 (same as the snapshot helpers).
 *   - Legacy lone `part.setupHrs` (no machine attached) is included in
 *     the setup amortization at a placeholder rate of 0 — i.e. it's
 *     ignored. Once every part has been migrated to MachineSetup, we can
 *     drop the legacy field. Until then, document that lone setupHrs
 *     doesn't move price (it was never billable in v1 either; it was a
 *     planning-only field).
 */
export function computePartCost(
  part: Part,
  qtyAtTier: number,
  tierIndex: number,
  machinesByName: Record<string, Machine>,
): PartCostBreakdown {
  // ───── material ─────
  let materialPerPiece = 0;
  if (part.customerSuppliedMaterial) {
    materialPerPiece = part.csmHandlingFeePerPiece ?? 0;
  } else if (part.materialCost) {
    materialPerPiece = normalizeMaterialCostPerPiece(
      part.materialCost,
      tierIndex,
      qtyAtTier,
    );
  }

  // ───── machine setup + run ─────
  let setupTotalCost = 0;
  let runPerPiece = 0;
  for (const ms of part.machines ?? []) {
    const m = machinesByName[ms.machine];
    if (!m) continue; // unmapped machine — silent skip, see fn header
    setupTotalCost += (ms.setupHrs ?? 0) * (m.setupRate ?? 0);
    const runHrs = ms.hrsPerTier?.[tierIndex] ?? 0;
    runPerPiece += runHrs * (m.runRate ?? 0);
  }
  // Legacy `part.setupHrs` is a planning-only field with no machine context
  // — we don't have a rate to apply, so it contributes $0. Documented above.

  const setupAmortPerPiece =
    qtyAtTier > 0 ? setupTotalCost / qtyAtTier : 0;

  // ───── outside services ─────
  // Two cost modes:
  //   lot_charge → flat per-batch fee amortized across qty (KMF default —
  //                anodize / heat treat / black oxide / grinding all bill
  //                per batch). lotCharge / qtyAtTier rolls into the setup
  //                amortization line.
  //   per_piece  → legacy setupCost + perPieceCost path; setupCost amortizes,
  //                perPieceCost stays per-piece.
  // Legacy rows without costMode default to per_piece so existing data still
  // prices the same way.
  let outsideSetupTotal = 0;
  let outsidePerPiece = 0;
  for (const os of part.outsideServices ?? []) {
    if (os.costMode === 'lot_charge') {
      // Flat per-batch fee — amortizes only (no per-piece line).
      outsideSetupTotal += os.lotCharge ?? 0;
    } else {
      outsideSetupTotal += os.setupCost ?? 0;
      outsidePerPiece += os.perPieceCost ?? 0;
    }
  }
  const outsideSetupPerPiece =
    qtyAtTier > 0 ? outsideSetupTotal / qtyAtTier : 0;

  const totalCostPerPiece =
    materialPerPiece
    + setupAmortPerPiece
    + runPerPiece
    + outsideSetupPerPiece
    + outsidePerPiece;

  return {
    materialPerPiece,
    setupAmortPerPiece,
    runPerPiece,
    outsideSetupPerPiece,
    outsidePerPiece,
    totalCostPerPiece,
  };
}

/**
 * Apply markup to a cost breakdown. Trivially `cost.total * markupPct` —
 * factored out so the seeder reads top-to-bottom and so a future "markup
 * by line item" variant has a place to land. Markup resolution (part-
 * level override → quote-level → default) is the caller's job; this just
 * does the multiply.
 */
export function suggestPricePerPiece(
  cost: PartCostBreakdown,
  markupPct: number,
): number {
  return cost.totalCostPerPiece * markupPct;
}

/**
 * Sum per-tier full cost across every part on the quote, using the live
 * cost engine. This is the cost-basis the TotalsCard / margin warning
 * should compare against — replaces the materialCost-only basis once the
 * caller is ready to commit. `computeMaterialCostByTier` is kept around
 * because the PDF exporter / older callers still depend on the
 * material-only line; both can co-exist.
 *
 * Missing parts contribute 0 for the affected tier. Missing machines on a
 * part contribute 0 to that machine's setup/run lines per `computePartCost`.
 */
export function computeFullCostByTier(
  quote: Quote,
  partsById: Record<string, Part>,
  machinesByName: Record<string, Machine>,
): number[] {
  const tierCount = quote.qtyTiers?.length ?? 0;
  const costs = new Array<number>(tierCount).fill(0);
  if (tierCount === 0) return costs;

  const qtyTiers = quote.qtyTiers ?? [];
  for (const partId of quote.partIds ?? []) {
    const part = partsById[partId];
    if (!part) continue;
    for (let t = 0; t < tierCount; t++) {
      const qty = qtyTiers[t] ?? 0;
      const bd = computePartCost(part, qty, t, machinesByName);
      costs[t] += bd.totalCostPerPiece;
    }
  }
  return costs;
}
